import React, { createContext, useContext, useState, useCallback } from 'react';
import { FiCheckCircle, FiXCircle, FiAlertTriangle, FiX, FiInfo } from 'react-icons/fi';

const ResponseModalContext = createContext(null);

export function useResponseModal() {
  const ctx = useContext(ResponseModalContext);
  if (!ctx) throw new Error('useResponseModal must be used within ResponseModalProvider');
  return ctx;
}

/**
 * showModal({ type, title, message, details, onConfirm, confirmLabel, cancelLabel })
 * type: 'success' | 'error' | 'warning' | 'confirm' | 'info'
 */
export function ResponseModalProvider({ children }) {
  const [modal, setModal] = useState(null);

  const showModal = useCallback((opts) => {
    setModal({ ...opts });
  }, []);

  const closeModal = useCallback(() => {
    setModal(null);
  }, []);

  const showSuccess = useCallback((message, title = 'Sucesso', details = null) => {
    setModal({ type: 'success', title, message, details });
  }, []);

  const showError = useCallback((message, title = 'Erro', details = null) => {
    setModal({ type: 'error', title, message, details });
  }, []);

  const showConfirm = useCallback((message, onConfirm, opts = {}) => {
    setModal({
      type: 'confirm',
      title: opts.title || 'Confirmar',
      message,
      onConfirm,
      confirmLabel: opts.confirmLabel || 'Confirmar',
      cancelLabel: opts.cancelLabel || 'Cancelar',
      dangerous: opts.dangerous || false,
    });
  }, []);

  const value = { showModal, showSuccess, showError, showConfirm, closeModal };

  return (
    <ResponseModalContext.Provider value={value}>
      {children}
      {modal && (
        <ResponseModal modal={modal} onClose={closeModal} />
      )}
    </ResponseModalContext.Provider>
  );
}

/* ---- The actual modal UI ---- */

const CONFIG = {
  success: {
    icon: FiCheckCircle,
    iconColor: '#16a34a',
    iconBg: '#dcfce7',
    titleColor: '#15803d',
    btnClass: 'btn-response-success',
  },
  error: {
    icon: FiXCircle,
    iconColor: '#dc2626',
    iconBg: '#fee2e2',
    titleColor: '#b91c1c',
    btnClass: 'btn-response-error',
  },
  warning: {
    icon: FiAlertTriangle,
    iconColor: '#d97706',
    iconBg: '#fef3c7',
    titleColor: '#92400e',
    btnClass: 'btn-response-warning',
  },
  confirm: {
    icon: FiAlertTriangle,
    iconColor: '#d97706',
    iconBg: '#fef3c7',
    titleColor: '#92400e',
    btnClass: 'btn-response-warning',
  },
  info: {
    icon: FiInfo,
    iconColor: '#2563eb',
    iconBg: '#dbeafe',
    titleColor: '#1e40af',
    btnClass: 'btn-response-info',
  },
};

function ResponseModal({ modal, onClose }) {
  const cfg = CONFIG[modal.type] || CONFIG.info;
  const Icon = cfg.icon;

  const handleConfirm = () => {
    if (modal.onConfirm) modal.onConfirm();
    onClose();
  };

  // Format details: array of strings or plain string
  const renderDetails = () => {
    if (!modal.details) return null;
    if (Array.isArray(modal.details)) {
      return (
        <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>
          {modal.details.map((d, i) => <li key={i}>{d}</li>)}
        </ul>
      );
    }
    if (typeof modal.details === 'object') {
      return (
        <pre style={{
          fontSize: '0.78rem', background: '#f8fafc', borderRadius: '8px',
          padding: '12px', overflowX: 'auto', maxHeight: '200px',
          color: 'var(--text-muted)', margin: 0, border: '1px solid var(--border)'
        }}>
          {JSON.stringify(modal.details, null, 2)}
        </pre>
      );
    }
    return (
      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
        {String(modal.details)}
      </p>
    );
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 99999, backdropFilter: 'blur(6px)',
        padding: '20px',
        animation: 'fadeInOverlay 0.18s ease',
      }}
      onClick={() => { }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '24px',
          width: '100%',
          maxWidth: '460px',
          boxShadow: '0 32px 80px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.04)',
          animation: 'responseModalIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
          overflow: 'hidden',
        }}
      >
        {/* Top accent bar */}
        <div style={{
          height: '4px',
          background: modal.type === 'success'
            ? 'linear-gradient(90deg, #16a34a, #4ade80)'
            : modal.type === 'error'
            ? 'linear-gradient(90deg, #dc2626, #f87171)'
            : modal.type === 'confirm' || modal.type === 'warning'
            ? 'linear-gradient(90deg, #d97706, #fbbf24)'
            : 'linear-gradient(90deg, #2563eb, #60a5fa)',
        }} />

        {/* Header */}
        <div style={{
          padding: '28px 28px 0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '52px', height: '52px', borderRadius: '16px',
              background: cfg.iconBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon size={26} color={cfg.iconColor} />
            </div>
            <div>
              <h4 style={{
                margin: 0, fontWeight: 700, fontSize: '1.05rem',
                color: cfg.titleColor, lineHeight: 1.3,
              }}>
                {modal.title}
              </h4>
            </div>
          </div>
          {modal.type !== 'confirm' && (
            <button
              onClick={onClose}
              style={{
                background: '#f1f5f9', border: 'none', cursor: 'pointer',
                width: '32px', height: '32px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
              onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
            >
              <FiX size={16} color="#64748b" />
            </button>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '20px 28px 0' }}>
          <p style={{
            margin: 0, fontSize: '0.95rem', color: '#374151',
            lineHeight: 1.65, fontWeight: 400,
          }}>
            {modal.message}
          </p>

          {modal.details && (
            <div style={{
              marginTop: '16px',
              padding: '14px',
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid var(--border)',
            }}>
              {renderDetails()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '24px 28px 28px',
          display: 'flex',
          justifyContent: modal.type === 'confirm' ? 'space-between' : 'flex-end',
          gap: '12px',
        }}>
          {modal.type === 'confirm' ? (
            <>
              <button
                onClick={onClose}
                style={{
                  padding: '10px 24px', borderRadius: '12px',
                  border: '1px solid var(--border)', background: 'white',
                  color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.9rem',
                  cursor: 'pointer', transition: 'all 0.15s',
                  flex: 1,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
              >
                {modal.cancelLabel || 'Cancelar'}
              </button>
              <button
                onClick={handleConfirm}
                style={{
                  padding: '10px 24px', borderRadius: '12px',
                  border: 'none', fontWeight: 700, fontSize: '0.9rem',
                  cursor: 'pointer', transition: 'all 0.15s',
                  flex: 1,
                  background: modal.dangerous
                    ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
                    : 'linear-gradient(135deg, #d97706, #b45309)',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                {modal.confirmLabel || 'Confirmar'}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              style={{
                padding: '10px 32px', borderRadius: '12px',
                border: 'none', fontWeight: 700, fontSize: '0.9rem',
                cursor: 'pointer', transition: 'all 0.15s',
                background: modal.type === 'success'
                  ? 'linear-gradient(135deg, #16a34a, #15803d)'
                  : modal.type === 'error'
                  ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
                  : 'linear-gradient(135deg, var(--primary), var(--primary-hover, #4338ca))',
                color: 'white',
                boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
              }}
            >
              OK, entendido
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeInOverlay {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes responseModalIn {
          from { opacity: 0; transform: scale(0.85) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
